'use client';

import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { toast } from 'react-toastify';
import { Icons } from '@/components/Icons';
import { Mail, MapPin, Clock, Send, Phone, AlertTriangle } from 'lucide-react';
import { usePageView } from '@/lib/hooks/usePageView';
import { SendContactUsEmail } from '@/lib/API/Services/contactUs/contactUs';
import TurnstileWidget from '@/components/TurnstileWidget';
import { trackContactFormSubmitted } from '@/lib/analytics/events';

export default function ContactUs() {
  usePageView();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    toast.error('Security verification failed. Please refresh and try again.');
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!turnstileToken) {
      toast.error('Please complete the security verification.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await SendContactUsEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject,
        message: formData.message,
        turnstileToken
      });
      
      if (result.success) {
        toast.success("Message sent! We'll get back to you soon.");
        trackContactFormSubmitted(formData.subject);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTurnstileToken(null);
      } else {
        toast.error(result.error || "Failed to send message. Please try again.");
      }
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      description: "hello@easypairing.com",
      link: "mailto:hello@easypairing.com"
    },   
    {
      icon: Clock,
      title: "Response Time",
      description: "Within 24 hours",
      link: null
    }
  ];

  return (
    <section className="w-full max-w-6xl mx-auto px-4 py-16 md:py-24">
      {/* Header */}
      <div className="text-center mb-16">
        <p className="text-amber-500 font-semibold text-sm uppercase tracking-wider mb-3">
          Contact Us
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
          Get in
          <span className="bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent">
            {" "}Touch
          </span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
          Have a question or need help? We&apos;d love to hear from you. Send us a message and we&apos;ll respond as soon as possible.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
        {/* Contact Info Cards */}
        <div className="lg:col-span-1 space-y-4">
          {contactInfo.map((info, index) => (
            <div 
              key={index}
              className="group p-6 rounded-2xl bg-slate-900/50 border border-white/5 hover:border-amber-600/20 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-700/15 group-hover:bg-amber-700/25 transition-colors">
                  <info.icon className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{info.title}</h3>
                  {info.link ? (
                    <a 
                      href={info.link}
                      className="text-sm text-slate-400 hover:text-amber-500 transition-colors"
                    >
                      {info.description}
                    </a>
                  ) : (
                    <p className="text-sm text-slate-400">{info.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Map/Location placeholder */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-700/10 to-amber-800/5 border border-amber-600/20">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-amber-700/20">
                <MapPin className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Office</h3>
                <p className="text-sm text-slate-400">                  
                 Houston, Texas
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2">
          <form 
            onSubmit={handleSubmit}
            className="p-8 rounded-2xl bg-slate-900/50 border border-white/5"
          >
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Your Name
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone Number <span className="text-slate-500 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Subject
                </label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="How can we help?"
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Message
              </label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us more about your needs..."
                required
                rows={6}
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-amber-600/50 focus:ring-amber-600/20 resize-none"
              />
            </div>

            {/* Cloudflare Turnstile */}
            <div className="mb-6 flex justify-center">
              <TurnstileWidget
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="dark"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-6 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold rounded-xl shadow-lg shadow-amber-900/25 hover:shadow-amber-900/40 transition-all duration-300"
            >
              {isSubmitting ? (
                <>
                  <Icons.Spinner className="mr-2 h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Send Message
                </>
              )}
            </Button>

            {/* Spam folder notice */}
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-400 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Please check your spam or junk folder if you don&apos;t receive our response within 24 hours.
                </span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
